import { Calendar } from '../../../assets/icons/Calendar';
import { DollarSmall } from '../../../assets/icons/DollarSmall';
import { WashingMachineSmall } from '../../../assets/icons/WashingMachineSmall';
import { Wrench } from '../../../assets/icons/Wrench';
import { ConfirmationPanel } from '../ConfirmationPage.types';

const formatPayment = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

type PurchasePlanLabels = {
  appliance: string;
  planNumber: string;
  planStartDate: string;
  monthlyPayment: string;
  monthlyPaymentFirstMonths: string;
  monthlyPaymentAfterMonths: string;
  panelTitle: string;
};

type RegistrationLabels = {
  appliance: string;
  modelNumber: string;
  serialNumber: string;
  purchaseDate: string;
  panelTitle: string;
};

export const formatPurchasedPlan = (
  brand: string,
  appliance?: string,
  startDate?: string,
  planNumber?: string,
  netFirstPaymentAmount?: number,
  netFirstPaymentAmountAfterPromotionalPeriod?: number,
  discountTerm?: number,
  labels?: PurchasePlanLabels,
): ConfirmationPanel | undefined => {
  if (!brand || !appliance || !startDate || !planNumber || netFirstPaymentAmount === undefined) {
    return undefined;
  }

  const formattedNetFirstPaymentAmount = formatPayment.format(netFirstPaymentAmount);
  const formattedPaymentAmountAfterPromotionalPeriod = netFirstPaymentAmountAfterPromotionalPeriod
    ? formatPayment.format(netFirstPaymentAmountAfterPromotionalPeriod)
    : undefined;

  const afterDiscountPayments = {
    label: labels?.monthlyPaymentAfterMonths ?? `Monthly Payment (after ${discountTerm} months)`,
    icon: DollarSmall(),
    value: String(formattedPaymentAmountAfterPromotionalPeriod),
  };

  const data = [
    {
      label: labels?.appliance ?? 'Appliance',
      icon: WashingMachineSmall(),
      value: `${brand} ${appliance}`,
    },
    {
      label: labels?.planNumber ?? 'Plan number',
      icon: Wrench(),
      value: planNumber,
    },
    {
      label: labels?.planStartDate ?? 'Plan start date',
      icon: Calendar(),
      value: startDate,
    },
    {
      label: discountTerm
        ? (labels?.monthlyPaymentFirstMonths ?? `Monthly Payment (first ${discountTerm} months)`)
        : (labels?.monthlyPayment ?? 'Monthly Payment'),
      icon: DollarSmall(),
      value: String(formattedNetFirstPaymentAmount),
    },
    ...(discountTerm && formattedPaymentAmountAfterPromotionalPeriod
      ? [afterDiscountPayments]
      : []),
  ];

  return {
    panelType: 'purchasePlan',
    panelTitle: labels?.panelTitle ?? 'Plan details',
    data,
  };
};

export const formatRegistrationData = (
  brand: string,
  appliance?: string,
  modelNumber?: string,
  serialNumber?: string,
  purchaseDate?: string,
  labels?: RegistrationLabels,
): ConfirmationPanel | undefined => {
  if (
    ![brand, appliance, modelNumber, serialNumber, purchaseDate].every(
      (value) => typeof value === 'string' && value.trim() !== '',
    )
  ) {
    return undefined;
  }

  return {
    panelType: 'registration',
    panelTitle: labels?.panelTitle ?? 'Registration details',
    data: [
      {
        label: labels?.appliance ?? 'Appliance',
        icon: WashingMachineSmall(),
        value: `${brand} ${appliance}`,
      },
      {
        label: labels?.modelNumber ?? 'Model number',
        icon: Wrench(),
        value: modelNumber,
      },
      {
        label: labels?.serialNumber ?? 'Serial number',
        icon: Wrench(),
        value: serialNumber,
      },
      {
        label: labels?.purchaseDate ?? 'Delivery date',
        icon: Calendar(),
        value: purchaseDate,
      },
    ],
  };
};
